import { formatWithValidation } from 'next/dist/shared/lib/router/utils/format-url';
import type NextLink from 'next/link';

import { type IconName } from '~/ui/components/icon';

/** Represents an href that can be passed to `<NextLink />` */
export type Href = React.ComponentProps<typeof NextLink>['href'];

/** Converts an {@link Href} to a string */
export function hrefToString(href: Href) {
  return typeof href === 'string' ? href : formatWithValidation(href);
}

/**
 * Checks if an href is internal.
 *
 * - Relative paths
 *   - `/`
 *   - `/ideas/artists-as-catalysts`
 * - Absolute paths:
 *   - `mellon.org/ideas/artists-as-catalysts`
 *   - `https://mellon.org`
 * - Url Objects (see {@link UrlObject})
 */
export function isInternalLink(href: Href) {
  const stringHref = hrefToString(href);
  return /^(\/([^\/]|$)|((https?:)?\/\/)?(www\.)?mellon\.org)/.test(stringHref);
}

/**
 * Resolves the "automatic" icon for a link
 *
 * - Email: `envelope`
 * - Phone number: `phone`
 * - PDF: `pdf`
 * - Internal link: `expanding-arrow-right`
 * - Other link: `expanding-arrow-diagonal`
 */
export function getLinkIcon(href: Href): IconName {
  const stringHref = hrefToString(href);
  if (stringHref.startsWith('mailto:')) {
    return 'envelope';
  }
  if (stringHref.startsWith('tel:')) {
    return 'phone';
  }
  if (stringHref.endsWith('.pdf')) {
    return 'pdf';
  }
  if (isInternalLink(href)) {
    return 'expanding-arrow-right';
  }
  return 'expanding-arrow-diagonal';
}
